import { useCallback, useState } from "react";
import { TurnstileWidget, turnstileEnabled } from "@blog/spa-common";
import { api, ApiError, ensureCsrf } from "@/lib/api";
import { Button, ErrorNote, Field, Panel, errorMessage, inputClass } from "@/components/ui";
import type { Recruiter } from "@/lib/types";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITEKEY ?? "";

// El portal vive en otro host que el blog, así que /contact va absoluto.
const CONTACT_URL = `${import.meta.env.VITE_BLOG_URL ?? "https://elcybercurioso.com"}/contact`;

export function Login({ onLogin }: { onLogin: (r: Recruiter) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Sin sitekey (dev) no hay widget y el servidor tampoco exige challenge.
  const needsChallenge = turnstileEnabled(SITE_KEY);

  // Estables: TurnstileWidget ya no depende de su identidad, pero pasarle
  // arrows inline seguiría creando trabajo inútil en cada render.
  const onTurnstile = useCallback((t: string) => setToken(t), []);
  const onTurnstileErr = useCallback(() => setToken(""), []);

  async function submit() {
    if (busy) return;
    setError(null);
    if (!username || !password) {
      setError("Rellena usuario y contraseña.");
      return;
    }
    if (needsChallenge && !token) {
      setError("Completa la verificación anti-bot.");
      return;
    }
    setBusy(true);
    try {
      // La cookie CSRF debe existir ANTES del POST de login: es el primer
      // envío del doble-submit y sin ella el servidor devuelve csrf_failed.
      await ensureCsrf();
      const res = await api<{ recruiter: Recruiter }>("/api/recruiter/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password, turnstileToken: token || undefined }),
      });
      onLogin(res.recruiter);
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-1 font-mono text-2xl text-ink">
        empleo<span className="text-accent">//</span>
      </h1>
      <p className="mb-6 text-sm text-mute">Portal de reclutadores · The RED Signal</p>

      <Panel>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label="usuario">
            <input
              className={inputClass}
              value={username}
              autoFocus
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
          <Field label="contraseña">
            <input
              className={inputClass}
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {needsChallenge && (
            <TurnstileWidget siteKey={SITE_KEY} onToken={onTurnstile} onError={onTurnstileErr} />
          )}

          <ErrorNote>{error}</ErrorNote>

          <Button type="submit" disabled={busy}>
            {busy ? "entrando…" : "entrar"}
          </Button>
        </form>
      </Panel>

      <p className="mt-6 text-xs text-mute">
        Las cuentas de reclutador las crea el administrador del sitio. Si no tienes acceso,
        solicítalo desde{" "}
        <a className="text-accent hover:underline" href={CONTACT_URL}>
          /contact
        </a>
        .
      </p>
    </div>
  );
}
